import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import inboxService from "@/services/mrfiktiv/services/inboxService";
import {
  MrfiktivCreateInboxDtoGen,
  MrfiktivInboxToolDtoGen,
  MrfiktivInboxViewModelGen,
  MrfiktivUpdateInboxDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { InboxDataAccessLayer } from "@/store/modules/access-layers/inbox.access-layer";

/**
 * Position of the user-supplied identifier within the local part of the inbox email address,
 * relative to the partner's company username.
 * - PREFIX:  <identifier><companyUsername>@<domain> (default, backwards compatible)
 * - POSTFIX: <companyUsername><identifier>@<domain>
 */
export enum IdentifierPosition {
  PREFIX = "prefix",
  POSTFIX = "postfix"
}

/**
 * Intersection of the generated create DTO with the new `identifierPosition` field.
 * The field is not yet in the auto-generated data-contracts.ts; it is sent on the wire
 * and consumed by the API. Remove this once the OpenAPI types are regenerated.
 */
type CreateInboxDtoWithIdentifierPosition = MrfiktivCreateInboxDtoGen & {
  identifierPosition?: IdentifierPosition;
};

@IsFilterable
class InboxBase implements IEntity<IInbox, MrfiktivUpdateInboxDtoGen>, MrfiktivInboxViewModelGen {
  loading = false;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.inbox.id"
  })
  id: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.inbox.partnerId"
  })
  partnerId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.source"
  })
  source: "email" | "sms" | "whatsapp";

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.name"
  })
  name: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.identifier"
  })
  identifier: string;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.inbox.isActive"
  })
  isActive: boolean;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.inbox.isPublic"
  })
  isPublic: boolean;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.notificationRecipients"
  })
  notificationRecipients: string[];

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.blockedSenders"
  })
  blockedSenders: string[];

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.inbox.isCompanyDomainAllowed"
  })
  isCompanyDomainAllowed: boolean;

  tools: MrfiktivInboxToolDtoGen[];

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  /**
   * Position of the user-supplied identifier in the inbox email address (form-only, not persisted on the entity).
   * Sent only on creation: PREFIX -> <identifier><companyUsername>@<domain>,
   * POSTFIX -> <companyUsername><identifier>@<domain>.
   */
  identifierPosition: IdentifierPosition;

  get icon(): string {
    switch (this.source) {
      case "email":
        return "mdi-email-outline";
      case "whatsapp":
        return "mdi-whatsapp";
      default:
        return "mdi-phone-outline";
    }
  }

  constructor(data?: Partial<MrfiktivInboxViewModelGen> & { identifierPosition?: IdentifierPosition }) {
    this.id = data?.id || "";
    this.partnerId = data?.partnerId || "";
    this.source = data?.source || "email";
    this.name = data?.name || "";
    this.identifier = data?.identifier || "";
    this.isActive = data?.isActive ?? false;
    this.isPublic = data?.isPublic ?? true;
    this.notificationRecipients = data?.notificationRecipients ? [...data.notificationRecipients] : [];
    this.blockedSenders = data?.blockedSenders ? [...data.blockedSenders] : [];
    this.isCompanyDomainAllowed = data?.isCompanyDomainAllowed ?? false;
    this.tools = data?.tools ? [...data.tools] : [];
    this.timestamp = new Timestamp(data?.timestamp);
    this.identifierPosition = data?.identifierPosition ?? IdentifierPosition.PREFIX;
  }

  map(data: MrfiktivInboxViewModelGen): void {
    this.id = data.id || "";
    this.partnerId = data.partnerId || "";
    this.source = data.source || "email";
    this.name = data.name || "";
    this.identifier = data.identifier || "";
    this.isActive = data.isActive ?? false;
    this.isPublic = data.isPublic ?? true;
    this.notificationRecipients = data.notificationRecipients ? [...data.notificationRecipients] : [];
    this.blockedSenders = data.blockedSenders ? [...data.blockedSenders] : [];
    this.isCompanyDomainAllowed = data.isCompanyDomainAllowed ?? false;
    this.tools = data.tools ? [...data.tools] : [];
    this.timestamp = new Timestamp(data.timestamp);
  }

  async create(): Promise<this> {
    const dto: CreateInboxDtoWithIdentifierPosition = {
      name: this.name,
      identifier: this.identifier || "",
      identifierPosition: this.identifierPosition,
      isActive: this.isActive,
      isPublic: this.isPublic,
      notificationRecipients: this.notificationRecipients,
      blockedSenders: this.blockedSenders,
      isCompanyDomainAllowed: this.isCompanyDomainAllowed
    };

    const res = await inboxService.create(this.partnerId, dto);

    this.map(res);
    InboxDataAccessLayer.set(this);

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await inboxService.getOne(this.partnerId, this.id);
      this.map(res);
      InboxDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      InboxDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivUpdateInboxDtoGen>): Promise<this> {
    const res = await inboxService.update(this.partnerId, this.id, dto);

    this.map(res);
    InboxDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      await this.updatePartial({
        source: this.source,
        name: this.name,
        identifier: this.identifier,
        isActive: this.isActive,
        isPublic: this.isPublic,
        notificationRecipients: this.notificationRecipients,
        blockedSenders: this.blockedSenders,
        isCompanyDomainAllowed: this.isCompanyDomainAllowed,
        tools: this.tools
      });
    } finally {
      this.loading = false;
    }

    return this;
  }

  upsertDocumentRuleCheckTool(isActive: boolean, ruleSetId: string | null): void {
    const others = this.tools.filter((t) => t.type !== "documentRuleCheck");
    this.tools = [
      ...others,
      {
        type: "documentRuleCheck",
        isActive,
        documentRuleCheckConfig: { ruleSetId }
      }
    ];
  }

  upsertCreateReportTool(isActive: boolean, isSummaryActive: boolean): void {
    const others = this.tools.filter((t) => t.type !== "createReport");
    this.tools = [
      ...others,
      {
        type: "createReport",
        isActive,
        createReportConfig: { isSummaryActive }
      }
    ];
  }

  upsertCreateInvoiceTool(isActive: boolean): void {
    const others = this.tools.filter((t) => t.type !== "createInvoice");
    this.tools = [
      ...others,
      {
        type: "createInvoice",
        isActive
      }
    ];
  }

  async delete(): Promise<void> {
    await inboxService.delete(this.partnerId, this.id);

    InboxDataAccessLayer.delete(this);
  }
}

type IInbox = InboxBase;
const Inbox = Filter.createForClass(InboxBase);

export { IInbox, Inbox };
